import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import { ValidationErrorSelector } from './components/error-selector';
import { ValidationSelector } from './components/validation-selector';
import { messages } from './messages';
import style from './style.module.less';
import ModalPortal from '../../../components/modal-portal';
import { AddressPropType } from '../../../prop-types/address';
import { FORM_REQUIREMENTS } from '../../utilities/constants';
import { useWalletRegistrationFormContext } from '../wallet-registration/contexts/wallet-registration-form-context';

const ERROR = 'Error';

export const AddressValidationModal = ({ addressResponse, advanceToNextStep, billingAddress, onClose }) => {
  const [selectSuggestedAddress, setSelectSuggestedAddress] = useState(true);
  const { walletForm, updateWalletForm, setIsVerifiedAddress } = useWalletRegistrationFormContext();
  const { matchedAddress, status } = addressResponse;

  const handleSaveAddress = useCallback(() => {
    const addressFromValidation = selectSuggestedAddress && !!matchedAddress ? matchedAddress : billingAddress;
    if (addressFromValidation === matchedAddress) {
      setIsVerifiedAddress(true);
    }
    const addressForWalletForm = {
      ...addressFromValidation,
      name: walletForm.BillingAddress.name,
      phone: walletForm.BillingAddress.phone,
      email: walletForm.BillingAddress.email,
    };
    updateWalletForm([FORM_REQUIREMENTS.BILLING_ADDRESS], addressForWalletForm);
    advanceToNextStep();
  }, [
    selectSuggestedAddress,
    matchedAddress,
    billingAddress,
    walletForm.BillingAddress.name,
    walletForm.BillingAddress.phone,
    walletForm.BillingAddress.email,
    updateWalletForm,
    advanceToNextStep,
    setIsVerifiedAddress,
  ]);
  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...messages.verifyYourAddress} />
        </ModalHeader>
        {addressResponse.status === ERROR && (
          <BannerMessageV2
            className={style['error-banner']}
            content={<FormattedMessage {...messages.errorDescription} />}
            status={BannerStatus.Error}
          />
        )}
        <ModalBody className={style['modal-body']}>
          {status !== ERROR ? (
            <ValidationSelector
              addressResponse={addressResponse}
              selectSuggestedAddress={selectSuggestedAddress}
              setSelectSuggestedAddress={setSelectSuggestedAddress}
            />
          ) : (
            <ValidationErrorSelector billingAddress={billingAddress} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button className={style['cancel-button']} onClick={onClose} theme='tertiary'>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button onClick={handleSaveAddress} theme='primary'>
            <FormattedMessage {...messages.saveAddress} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

AddressValidationModal.propTypes = {
  addressResponse: PropTypes.shape({
    matchedAddress: AddressPropType,
    status: PropTypes.string,
  }).isRequired,
  billingAddress: AddressPropType.isRequired,
  advanceToNextStep: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
